import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LanguageIcon from '@mui/icons-material/Language';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const SalesSiteDomains = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDomain, setEditingDomain] = useState(null);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordAction, setPasswordAction] = useState(null); // 'add', 'edit', 'delete'
  const [developerPassword, setDeveloperPassword] = useState('');
  const [openSslDialog, setOpenSslDialog] = useState(false);
  const [sslTargetDomain, setSslTargetDomain] = useState(null);
  const [sslSshPassword, setSslSshPassword] = useState('');
  const [sshChecking, setSshChecking] = useState(false);
  const [sshChecked, setSshChecked] = useState(false);
  const [formData, setFormData] = useState({
    domain: '',
    isPrimary: false,
    sslEnabled: false,
    type: 'main',
    serverIp: '',
    sshLogin: '',
    sshPassword: ''
  });

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      const response = await axios.get(`${API_URL}sales-site/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(response.data.data || []);
    } catch (error) {
      console.error('Error loading domains:', error);
      enqueueSnackbar('Ошибка загрузки доменов', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (domain = null) => {
    if (domain) {
      setEditingDomain(domain);
      setFormData({
        domain: domain.domain,
        isPrimary: domain.isPrimary,
        sslEnabled: domain.sslEnabled,
        type: domain.type || 'main',
        serverIp: domain.serverIp || '',
        sshLogin: domain.sshLogin || '',
        sshPassword: ''
      });
      setSshChecked(true);
    } else {
      setEditingDomain(null);
      setFormData({
        domain: '',
        isPrimary: false,
        sslEnabled: false,
        type: 'main',
        serverIp: '',
        sshLogin: '',
        sshPassword: ''
      });
      setSshChecked(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDomain(null);
    setFormData({
      domain: '',
      isPrimary: false,
      sslEnabled: false,
      type: 'main',
      serverIp: '',
      sshLogin: '',
      sshPassword: ''
    });
    setSshChecked(false);
  };

  const handleCheckSsh = async () => {
    try {
      if (!formData.serverIp || !formData.sshLogin || !formData.sshPassword) {
        enqueueSnackbar('Укажите IP, SSH логин и пароль', { variant: 'warning' });
        return;
      }

      setSshChecking(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

      await axios.post(
        `${API_URL}sales-site/domains/check-ssh`,
        {
          serverIp: formData.serverIp,
          sshLogin: formData.sshLogin,
          sshPassword: formData.sshPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSshChecked(true);
      enqueueSnackbar('SSH подключение успешно', { variant: 'success' });
    } catch (error) {
      setSshChecked(false);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка SSH подключения', { variant: 'error' });
    } finally {
      setSshChecking(false);
    }
  };

  const handleSave = async () => {
    if (!editingDomain && formData.type === 'mirror') {
      if (!formData.serverIp || !formData.sshLogin || !formData.sshPassword) {
        enqueueSnackbar('Для зеркала заполните IP, SSH логин и пароль', { variant: 'warning' });
        return;
      }

      if (!sshChecked) {
        enqueueSnackbar('Сначала проверьте SSH подключение', { variant: 'warning' });
        return;
      }

      await performBindMirror();
      return;
    }

    // Проверяем нужен ли пароль (для основного домена)
    const isMainDomain = formData.type === 'main' || (!editingDomain && domains.length === 0);
    const isEditingMainDomain = editingDomain && editingDomain.type === 'main';
    
    if (isMainDomain || isEditingMainDomain) {
      setPasswordAction(editingDomain ? 'edit' : 'add');
      setOpenPasswordDialog(true);
      return;
    }
    
    // Если не основной домен, сохраняем без пароля
    await performSave();
  };

  const performBindMirror = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

      const response = await axios.post(
        `${API_URL}sales-site/domains/bind-mirror`,
        {
          domain: formData.domain,
          serverIp: formData.serverIp,
          sshLogin: formData.sshLogin,
          sshPassword: formData.sshPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      enqueueSnackbar(response.data?.message || 'Зеркало привязано и проксирование настроено', { variant: 'success' });
      handleCloseDialog();
      loadDomains();
    } catch (error) {
      console.error('Error binding mirror domain:', error);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка привязки зеркала', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const performSave = async (password = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      
      const dataToSend = { ...formData };
      if (password) {
        dataToSend.developerPassword = password;
      }
      
      if (editingDomain) {
        // Обновление домена
        await axios.put(`${API_URL}sales-site/domains/${editingDomain._id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        enqueueSnackbar('Домен успешно обновлен', { variant: 'success' });
      } else {
        // Добавление нового домена
        await axios.post(`${API_URL}sales-site/domains`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
        enqueueSnackbar('Домен успешно добавлен и Nginx настроен', { variant: 'success' });
      }
      
      handleCloseDialog();
      setOpenPasswordDialog(false);
      setDeveloperPassword('');
      loadDomains();
    } catch (error) {
      console.error('Error saving domain:', error);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка сохранения домена', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const domain = domains.find(d => d._id === id);
    if (!domain) return;
    
    // Если это основной домен, требуем пароль
    if (domain.type === 'main') {
      setPasswordAction('delete');
      setEditingDomain(domain);
      setOpenPasswordDialog(true);
      return;
    }
    
    // Для зеркал просто подтверждение
    if (!window.confirm('Вы уверены, что хотите удалить этот домен?')) {
      return;
    }
    
    await performDelete(id);
  };

  const performDelete = async (id, password = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      
      if (password) {
        config.data = { developerPassword: password };
      }
      
      await axios.delete(`${API_URL}sales-site/domains/${id}`, config);
      
      enqueueSnackbar('Домен и конфигурация Nginx успешно удалены', { variant: 'success' });
      setOpenPasswordDialog(false);
      setDeveloperPassword('');
      setEditingDomain(null);
      loadDomains();
    } catch (error) {
      console.error('Error deleting domain:', error);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка удаления домена', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCopySymlink = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      
      const response = await axios.get(`${API_URL}sales-site/domains/${id}/symlink-command`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.command) {
        navigator.clipboard.writeText(response.data.command);
        enqueueSnackbar('Команда симлинка скопирована! Выполните на сервере.', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error getting symlink command:', error);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка получения команды', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSSL = async (domain, sshPassword = '') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      
      const payload = {};
      if (domain.type === 'mirror') {
        payload.sshPassword = sshPassword;
      }

      const response = await axios.post(`${API_URL}sales-site/domains/${domain._id}/request-ssl`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (domain.type !== 'mirror' && response.data.command) {
        navigator.clipboard.writeText(response.data.command);
        enqueueSnackbar('Команда скопирована! Выполните её на сервере.', { variant: 'success' });
      } else {
        enqueueSnackbar(response.data.message || 'Запрос SSL отправлен', { variant: 'success' });
      }
      setOpenSslDialog(false);
      setSslTargetDomain(null);
      setSslSshPassword('');
      loadDomains();
    } catch (error) {
      console.error('Error requesting SSL:', error);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка запроса SSL', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSslRequest = (domain) => {
    if (domain.type === 'mirror') {
      setSslTargetDomain(domain);
      setSslSshPassword('');
      setOpenSslDialog(true);
      return;
    }

    handleRequestSSL(domain);
  };

  const handleCheckSSL = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
      
      const response = await axios.post(`${API_URL}sales-site/domains/${id}/check-ssl`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const sslInfo = response.data.data;
      
      if (sslInfo.enabled) {
        enqueueSnackbar(`SSL активен. Осталось ${sslInfo.daysRemaining} дней до истечения`, { variant: 'success' });
      } else {
        enqueueSnackbar('SSL сертификат не найден', { variant: 'warning' });
      }
      
      loadDomains();
    } catch (error) {
      console.error('Error checking SSL:', error);
      enqueueSnackbar(error.response?.data?.error || 'Ошибка проверки SSL', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LanguageIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Домены
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Добавить домен
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Основной домен:</strong> Настройте NS записи у регистратора доменов на наши DNS серверы.
        <br />
        <strong>Зеркала:</strong> Укажите домен + SSH доступ, проверьте подключение и нажмите привязать.
      </Alert>

      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Домен</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>SSL</TableCell>
                  <TableCell>Дата добавления</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {domains.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        Нет добавленных доменов
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  domains.map((domain) => (
                    <TableRow key={domain._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LanguageIcon fontSize="small" color="action" />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {domain.domain}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={domain.type === 'main' ? 'Основной' : 'Зеркало'}
                          color={domain.type === 'main' ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={domain.status === 'active' ? <CheckCircleIcon /> : <ErrorIcon />}
                          label={domain.status === 'active' ? 'Активен' : 'Неактивен'}
                          color={domain.status === 'active' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={domain.sslEnabled ? 'Включен' : 'Выключен'}
                          color={domain.sslEnabled ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(domain.createdAt).toLocaleDateString('ru-RU')}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                          <IconButton
                            size="small"
                            onClick={() => handleCopySymlink(domain._id)}
                            disabled={loading}
                            title="Скопировать команду симлинка"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCheckSSL(domain._id)}
                            disabled={loading}
                            title="Проверить SSL"
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LockIcon fontSize="small" />}
                            onClick={() => handleOpenSslRequest(domain)}
                            disabled={loading}
                          >
                            Запросить SSL
                          </Button>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(domain)}
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(domain._id)}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDomain ? 'Редактировать домен' : 'Добавить домен'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Домен"
              value={formData.domain}
              onChange={(e) => {
                setFormData({ ...formData, domain: e.target.value });
                setSshChecked(false);
              }}
              placeholder="example.com"
              disabled={loading}
            />

            <FormControl fullWidth>
              <InputLabel>Тип домена</InputLabel>
              <Select
                value={formData.type}
                label="Тип домена"
                onChange={(e) => {
                  const nextType = e.target.value;
                  setFormData({ ...formData, type: nextType });
                  if (nextType !== 'mirror') {
                    setSshChecked(false);
                  }
                }}
                disabled={loading || !!editingDomain}
              >
                <MenuItem value="main">Основной</MenuItem>
                <MenuItem value="mirror">Зеркало</MenuItem>
              </Select>
            </FormControl>

            {formData.type === 'mirror' && (
              <>
                <TextField
                  fullWidth
                  label="IP сервера"
                  value={formData.serverIp}
                  onChange={(e) => {
                    setFormData({ ...formData, serverIp: e.target.value });
                    setSshChecked(false);
                  }}
                  placeholder="1.2.3.4"
                  disabled={loading}
                />
                <TextField
                  fullWidth
                  label="SSH логин"
                  value={formData.sshLogin}
                  onChange={(e) => {
                    setFormData({ ...formData, sshLogin: e.target.value });
                    setSshChecked(false);
                  }}
                  placeholder="root"
                  disabled={loading}
                />
                <TextField
                  fullWidth
                  label="SSH пароль"
                  type="password"
                  value={formData.sshPassword}
                  onChange={(e) => {
                    setFormData({ ...formData, sshPassword: e.target.value });
                    setSshChecked(false);
                  }}
                  disabled={loading}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCheckSsh}
                    disabled={loading || sshChecking || !formData.serverIp || !formData.sshLogin || !formData.sshPassword}
                  >
                    {sshChecking ? 'Проверка...' : 'Проверить подключение'}
                  </Button>
                  {sshChecked && <Chip color="success" size="small" label="SSH OK" />}
                </Box>
              </>
            )}

            <Alert severity="info">
              {formData.type === 'main'
                ? 'Для настройки основного домена обратитесь к разработчику'
                : 'DNS уже должен указывать на ваш сервер. После привязки будет настроено зеркало на основной домен.'}
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Отмена
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={
              loading ||
              !formData.domain ||
              (formData.type === 'mirror' && (!formData.serverIp || !formData.sshLogin || !formData.sshPassword))
            }
          >
            {editingDomain ? 'Сохранить' : formData.type === 'mirror' ? 'Привязать' : 'Добавить'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог ввода пароля разработчика */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Требуется пароль разработчика
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Для {passwordAction === 'add' ? 'добавления' : passwordAction === 'edit' ? 'редактирования' : 'удаления'} основного домена требуется пароль разработчика
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="Пароль разработчика"
            type="password"
            fullWidth
            value={developerPassword}
            onChange={(e) => setDeveloperPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                if (passwordAction === 'delete') {
                  performDelete(editingDomain._id, developerPassword);
                } else {
                  performSave(developerPassword);
                }
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenPasswordDialog(false);
            setDeveloperPassword('');
          }}>
            Отмена
          </Button>
          <Button
            onClick={() => {
              if (passwordAction === 'delete') {
                performDelete(editingDomain._id, developerPassword);
              } else {
                performSave(developerPassword);
              }
            }}
            variant="contained"
            disabled={loading || !developerPassword}
          >
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSslDialog} onClose={() => setOpenSslDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Запрос SSL для зеркала</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Укажите SSH пароль для домена {sslTargetDomain?.domain}
          </Alert>
          <TextField
            autoFocus
            margin="dense"
            label="SSH пароль"
            type="password"
            fullWidth
            value={sslSshPassword}
            onChange={(e) => setSslSshPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && sslTargetDomain && sslSshPassword) {
                handleRequestSSL(sslTargetDomain, sslSshPassword);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenSslDialog(false);
              setSslTargetDomain(null);
              setSslSshPassword('');
            }}
            disabled={loading}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={() => sslTargetDomain && handleRequestSSL(sslTargetDomain, sslSshPassword)}
            disabled={loading || !sslTargetDomain || !sslSshPassword}
          >
            Запросить SSL
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesSiteDomains;
